import { useCallback, useEffect, useRef, useState } from "react";
import { PersonSimpleWalk, Play, Pause, SkipForward, X, Sparkle, Trash, Plus, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAccess } from "@/context/AccessContext";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const beep = () => {
  try {
    if (navigator.vibrate) navigator.vibrate(180);
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 660;
    gain.gain.value = 0.08;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, 180);
  } catch { /* silent on locked audio */ }
};

/** Step-by-step player with a hold countdown, a beep and a vibration between moves. */
const GuidedTimer = ({ routine, onClose, onDone }) => {
  const { announce, prefs } = useAccess();
  const [step, setStep] = useState(0);
  const [left, setLeft] = useState(routine.steps[0]?.seconds || 30);
  const [running, setRunning] = useState(true);
  const steps = routine.steps || [];
  const doneRef = useRef(false);

  const next = useCallback(() => {
    beep();
    setStep((s) => {
      const upcoming = s + 1;
      if (upcoming >= steps.length) {
        if (!doneRef.current) { doneRef.current = true; announce("Routine finished. Nice work."); onDone(); }
        return s;
      }
      setLeft(steps[upcoming].seconds);
      announce(`Next move: ${steps[upcoming].move}, ${steps[upcoming].seconds} seconds`);
      return upcoming;
    });
  }, [steps, onDone, announce]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setLeft((v) => (v <= 1 ? (next(), steps[step]?.seconds || 30) : v - 1)), 1000);
    return () => clearInterval(id);
  }, [running, next, step, steps]);

  const current = steps[step] || {};
  const finished = doneRef.current;

  return (
    <div className="fixed inset-0 z-[70] flex items-end bg-black/70 sm:items-center sm:justify-center"
      data-testid="stretch-timer" role="dialog" aria-label={`Guided routine: ${routine.name}`}>
      <div className={`w-full bg-[#0F172A] p-6 text-white sm:max-w-md ${
        !prefs.reduceMotion && left <= 3 && !finished ? "animate-pulse" : ""}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="overline text-[#7FA0FF]">{routine.name}</p>
            <p className="mt-1 text-sm text-slate-300">
              Move {Math.min(step + 1, steps.length)} of {steps.length} · {routine.where}
            </p>
          </div>
          <button type="button" data-testid="stretch-timer-close" aria-label="Close" onClick={onClose}
            className="flex h-10 w-10 items-center justify-center text-slate-400">
            <X size={20} />
          </button>
        </div>

        {finished ? (
          <p className="mt-8 text-center font-display text-3xl font-bold" data-testid="stretch-timer-done">
            Done — nice work.
          </p>
        ) : (
          <>
            <p className="font-display mt-6 text-2xl font-bold tracking-tight" data-testid="stretch-timer-move">
              {current.move}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-slate-300">{current.how}</p>
            <p className="font-display mt-6 text-center text-6xl font-bold tabular-nums"
              data-testid="stretch-timer-count" aria-live="polite" aria-label={`${left} seconds left`}>
              {left}
            </p>
            <div className="mt-6 flex gap-2">
              <Button className="flex-1" data-testid="stretch-timer-toggle" onClick={() => setRunning((r) => !r)}>
                {running ? <><Pause size={15} className="mr-1.5" /> Hold</> : <><Play size={15} className="mr-1.5" /> Go on</>}
              </Button>
              <Button variant="outline" className="flex-1 border-white/25 bg-transparent text-white hover:bg-white/10"
                data-testid="stretch-timer-skip" onClick={next}>
                <SkipForward size={15} className="mr-1.5" /> Next move
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const BLANK = { name: "", minutes: "5", where: "on the floor", steps: [{ move: "", seconds: "30", how: "" }] };

export const Stretching = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [check, setCheck] = useState({});
  const [board, setBoard] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/stretch").then(({ data: d }) => setData(d)).catch(oops);
    api.get("/stretch/leaderboard").then(({ data: d }) => setBoard(d)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const finish = async (routine) => {
    try {
      const { data: d } = await api.post("/stretch/sessions", { routine_id: routine.id, minutes: routine.minutes });
      toast.success(`+${d.earned} points · ${d.points.points} total`);
      load();
    } catch (e) { oops(e); }
  };

  const saveCheck = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/stretch/rom", { areas: check });
      toast.success(`Range of motion ${d.score}% · +${d.earned} points`);
      setCheck({});
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const suggestOne = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/stretch/suggest");
      setForm({
        name: d.routine.name || `Routine for ${d.area_label}`,
        minutes: String(d.routine.minutes || 5),
        where: d.routine.where || "on the floor",
        steps: (d.routine.steps || []).map((s) => ({ move: s.move, seconds: String(s.seconds || 30), how: s.how || "" })),
      });
      setAdding(true);
      toast.success(`Written for your ${d.area_label.toLowerCase()}`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const saveRoutine = async () => {
    setBusy(true);
    try {
      await api.post("/stretch/routines", {
        name: form.name, minutes: Number(form.minutes) || 5, where: form.where, focus: [],
        steps: form.steps.filter((s) => s.move.trim()).map((s) => ({
          move: s.move, seconds: Number(s.seconds) || 30, how: s.how })),
        shared: false,
      });
      toast.success("Routine saved");
      setForm(BLANK); setAdding(false); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const removeRoutine = async (id) => {
    try { await api.delete(`/stretch/routines/${id}`); toast.success("Removed"); load(); }
    catch (e) { oops(e); }
  };

  if (!data) return <p className="py-8 text-center text-sm text-slate-500">Loading your stretching…</p>;

  const p = data.points;

  return (
    <div className="space-y-5" data-testid="stretching-tab">
      <div className="grid gap-3 sm:grid-cols-4" data-testid="stretch-points">
        {[["Points", p.points], ["Routines done", p.routines_done],
          ["Day streak", p.streak_days], ["Bonus earned", p.bonus]].map(([label, value]) => (
          <div key={label} className="border border-border bg-white p-3">
            <p className="overline text-slate-500">{label}</p>
            <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-slate-600" data-testid="stretch-next-bonus">
        <Trophy size={14} className="mr-1 inline align-[-2px] text-[#B77B00]" />{p.next_bonus}
      </p>

      {data.routine_of_the_day && (
        <div className="border border-[#002FA7] bg-white p-4" data-testid="stretch-of-the-day">
          <p className="overline text-[#002FA7]">Stretch of the day{data.day ? ` · ${data.day}` : ""}</p>
          <p className="mt-1 text-base font-semibold text-[#0F172A]">{data.routine_of_the_day.name}</p>
          <p className="text-sm text-slate-600">
            {data.routine_of_the_day.minutes} min · {data.routine_of_the_day.where}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" data-testid="stretch-play-today"
              onClick={() => setPlaying(data.routine_of_the_day)}>
              <Play size={14} className="mr-1.5" /> Start it
            </Button>
            <ShareBar url={`${window.location.origin}/stretching`}
              text={`Stretch of the day — ${data.routine_of_the_day.name}`}
              testid="stretch-day-share" compact instagram />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" data-testid="stretch-suggest" onClick={suggestOne} disabled={busy}>
          <Sparkle size={14} className="mr-1.5" /> Write me one for my tightest bit
        </Button>
        <Button variant="outline" size="sm" data-testid="stretch-add-open" onClick={() => setAdding((v) => !v)}>
          <Plus size={14} className="mr-1.5" /> My own routine
        </Button>
        <a href="/stretching" target="_blank" rel="noreferrer" data-testid="stretch-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public stretching board
        </a>
      </div>

      {adding && (
        <div className="border border-border bg-white p-4" data-testid="stretch-add-form">
          <div className="grid gap-2 sm:grid-cols-3">
            <Input data-testid="stretch-form-name" placeholder="Routine name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="stretch-form-minutes" type="number" min="1" max="30" placeholder="Minutes"
              value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} />
            <Input data-testid="stretch-form-where" placeholder="Where (in the cab…)" value={form.where}
              onChange={(e) => setForm({ ...form, where: e.target.value })} />
          </div>
          <div className="mt-3 space-y-2">
            {form.steps.map((s, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[2fr_1fr_3fr]">
                <Input data-testid={`stretch-step-move-${i}`} placeholder="Move" value={s.move}
                  onChange={(e) => setForm({ ...form, steps: form.steps.map((x, n) => n === i ? { ...x, move: e.target.value } : x) })} />
                <Input data-testid={`stretch-step-seconds-${i}`} type="number" min="5" max="300" placeholder="Seconds" value={s.seconds}
                  onChange={(e) => setForm({ ...form, steps: form.steps.map((x, n) => n === i ? { ...x, seconds: e.target.value } : x) })} />
                <Input data-testid={`stretch-step-how-${i}`} placeholder="How to do it" value={s.how}
                  onChange={(e) => setForm({ ...form, steps: form.steps.map((x, n) => n === i ? { ...x, how: e.target.value } : x) })} />
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="stretch-step-add"
              onClick={() => setForm({ ...form, steps: [...form.steps, { move: "", seconds: "30", how: "" }] })}>
              Add a move
            </Button>
            <Button size="sm" data-testid="stretch-form-save" onClick={saveRoutine}
              disabled={busy || form.name.trim().length < 3}>Save the routine</Button>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2" data-testid="stretch-routines">
        {data.routines.map((r) => (
          <div key={r.id} data-testid={`stretch-routine-${r.id}`} className="border border-border bg-white">
            {r.image_url && (
              <img src={`${BACKEND}${r.image_url}`} alt={r.name} loading="lazy"
                className="h-32 w-full object-cover" />
            )}
            <div className="p-4">
              <p className="text-sm font-semibold text-[#0F172A]">{r.name}</p>
              <p className="text-xs text-slate-500">
                {r.minutes} min · {r.where} · {(r.steps || []).length} moves
                {r.author_id !== "system" && !r.shared ? " · yours" : ""}
              </p>
              <ul className="mt-2 space-y-0.5 text-sm text-slate-700">
                {(r.steps || []).slice(0, 3).map((s, i) => <li key={i}>· {s.move} — {s.seconds}s</li>)}
                {(r.steps || []).length > 3 && <li className="text-slate-400">· and {(r.steps || []).length - 3} more</li>}
              </ul>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" data-testid={`stretch-play-${r.id}`} onClick={() => setPlaying(r)}>
                  <Play size={14} className="mr-1.5" /> Guide me
                </Button>
                {(user?.role === "admin" || r.author_id === user?.id) && (
                  <Button size="sm" variant="outline" data-testid={`stretch-delete-${r.id}`}
                    onClick={() => removeRoutine(r.id)}>
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-border bg-white p-4" data-testid="stretch-rom">
        <p className="overline text-slate-500">Range of motion self-check</p>
        <p className="mt-1 text-sm text-slate-600">
          Rate each area easy, tight or painful. {data.rom.note}
        </p>
        <div className="mt-3 space-y-2">
          {Object.entries(data.areas).map(([key, label]) => (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
              <span className="text-sm font-medium text-[#0F172A]">{label}</span>
              <div className="flex gap-1.5">
                {data.ratings.map((rating) => (
                  <button key={rating} type="button" data-testid={`rom-${key}-${rating}`}
                    onClick={() => setCheck({ ...check, [key]: rating })}
                    className={`tap-safe border px-2.5 py-1.5 text-xs font-semibold capitalize transition-colors duration-150 ${
                      check[key] === rating ? "border-[#0F172A] bg-[#0F172A] text-white"
                        : "border-border bg-white text-slate-600 hover:border-[#0F172A]"}`}>
                    {rating}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button className="mt-3" size="sm" data-testid="rom-save" onClick={saveCheck}
          disabled={busy || Object.keys(check).length === 0}>
          Save the check
        </Button>
        {data.rom.latest && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="rom-latest">
            <p className="text-sm text-[#0F172A]">
              Last check {data.rom.latest.day} · score {data.rom.latest.score}%
              {data.rom.tightest_label ? ` · tightest: ${data.rom.tightest_label}` : ""}
            </p>
            {data.rom.refer && (
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#8E1C15]" data-testid="rom-refer">
                Pain is not a stretch to push through — speak to a therapist
              </p>
            )}
          </div>
        )}
      </div>

      {board?.people?.length > 0 && (
        <div className="border border-border bg-white p-4" data-testid="stretch-leaderboard">
          <p className="text-sm font-semibold text-[#0F172A]">Most routines this month</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {board.people.slice(0, 5).map((row, i) => (
              <li key={row.user_id}>{i + 1}. {row.name} · {row.sessions} routines · {row.points} points</li>
            ))}
          </ul>
        </div>
      )}

      {playing && (
        <GuidedTimer routine={playing} onClose={() => setPlaying(null)} onDone={() => finish(playing)} />
      )}
    </div>
  );
};

export const StretchIcon = PersonSimpleWalk;
